import { config } from '../../../../../config';
import DigiobcanApi, { type Issue } from '../../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import { getReportCategories } from '../../../../features/queries/getReportCategories';
import { useM, useQ } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import IssueSection from './IssueSection';
import { useAuthRoles } from '@presov.sk/presov-auth-react';
import { Button, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  issue: Issue;
  /** Ostatné nálezy podnetu - z nich sa vie, či podnet pôjde na triáž. */
  siblings: Issue[];
  /** Obnova obálky aj nálezov - nález po odstránení mení stav. */
  onDone: () => Promise<unknown>;
  /** Vyjadrenie k nálezu je v schvaľovaní (čaká alebo je vrátené). */
  statementInReview: boolean;
  /** Do nálezu sú zlúčené duplikáty iných podnetov. */
  hasDuplicates: boolean;
  /** Mobilná obrazovka nálezu - bez karty sekcie. */
  compact?: boolean;
};

/**
 * „Odstrániť z kategórie" - oprava omylu, keď úradník podnet prijal (alebo
 * nechal ležať) v kategórii, kam nepatrí (moj-presov#25). Dovtedy sa to dalo
 * len ručne v databáze.
 *
 * **Nie je to krok rozhodovania**, preto nestojí v pätičke ani
 * v [[buildDecision]]: rozhodnutie je bežná práca s nálezom, toto je zásah
 * mimo nej s vlastným oprávnením `pruser:reports:issues:remove`. Tvarom je to
 * dvojča archivácie podnetu ([[ReportArchive]]) - nadpis, veta dôsledku
 * a červené tlačidlo za potvrdzovacím dialógom - len na spodku karty nálezu,
 * nie obálky.
 *
 * Nález sa v API nemaže, prejde do stavu Presunuté, takže karta po uložení
 * zostane a ukáže „Znovu prijať nález". Ak podnet nemá iný nález mimo
 * presunutých, dostane nový v predvolenej kategórii (triáž). Obrazovka to vie
 * dopredu zo `siblings`, aby to dialóg mohol povedať; rozhoduje však API.
 *
 * Keď odstránenie nejde (vyjadrenie v schvaľovaní, zlúčené duplikáty),
 * tlačidlo zostane a je vypnuté s dôvodom - schovať ho by znamenalo, že
 * úradník nevie, prečo tam nie je. Predvolená kategória je výnimka: tam sa
 * nevykreslí vôbec ([[IssueDetail]]), lebo z triáže sa podnet posiela ďalej
 * prekategorizovaním, nie odstraňuje.
 */
const IssueRemove: React.FC<Props> = ({
  issue,
  siblings,
  onDone,
  statementInReview,
  hasDuplicates,
  compact = false,
}) => {
  const { logger } = useLogger();
  const { hasPermission } = useAuthRoles();
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const canRemove = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:issues:remove',
      }),
    [hasPermission],
  );

  const { data: categories = [] } = useQ(getReportCategories, undefined, {
    staleTime: 5 * 60 * 1000,
  });
  const defaultCategory = categories.find((category) => category.is_default);

  // Rovnaká podmienka ako v API: na triáž ide podnet, ktorému nezostane nič
  // mimo presunutých - ani `open` v inej kategórii.
  const triage = !siblings.some(
    (sibling) => sibling.id !== issue.id && sibling.status !== 'moved',
  );

  const blockedReason = statementInReview
    ? __(
        'Vyjadrenie k nálezu je v schvaľovaní. Odstrániť ho pôjde, keď sa o ňom rozhodne alebo ho autor stiahne.',
      )
    : hasDuplicates
      ? __(
          'Do nálezu sú zlúčené duplikáty iných podnetov a riešia sa s ním, preto ho z kategórie odstrániť nemožno.',
        )
      : undefined;

  const mutation = useM(() => DigiobcanApi.adminRemoveIssue(issue.id), {
    onSuccess: async ({ triage: wentToTriage }) => {
      await onDone();
      setConfirmOpen(false);
      toastSuccess(
        wentToTriage
          ? __('Podnet bol odstránený z kategórie a vrátil sa na triáž.')
          : __('Podnet bol odstránený z kategórie.'),
      );
    },
    onError: (error) => {
      logger.error('Failed to remove issue from category', {
        action: 'removeIssue',
        error,
        data: { issueId: issue.id, categoryId: issue.category_id },
      });

      const name = error.details?.name;
      if (name === 'STATEMENT_PENDING_APPROVAL') {
        toastError(
          __('Vyjadrenie k nálezu čaká na schválenie. Obnovte stránku.'),
        );
      } else if (name === 'ISSUE_HAS_DUPLICATES') {
        toastError(
          __('Do nálezu sú zlúčené duplikáty, odstrániť ho preto nemožno.'),
        );
      } else if (name === 'CATEGORY_IS_DEFAULT') {
        toastError(
          __(
            'Podnet nemá inú kategóriu a táto je predvolená. Prekategorizujte ho.',
          ),
        );
      } else if (name === 'INVALID_ISSUE_STATUS_UPDATE') {
        toastError(__('Stav nálezu sa medzitým zmenil. Obnovte stránku.'));
      } else if (name === 'REPORT_ARCHIVED') {
        toastError(__('Podnet je archivovaný.'));
      } else if (name === 'FORBIDDEN_ISSUE_STATE_UPDATE') {
        toastError(__('K tejto kategórii nemáte prístup.'));
      } else {
        toastError(__('Podnet sa nepodarilo odstrániť z kategórie.'));
      }
      setConfirmOpen(false);
    },
  });

  if (!canRemove) return null;

  const description = __(
    'Keď sem podnet nepatrí a prijali ho omylom. Nález zostane v histórii ako presunutý a dá sa znovu prijať.',
  );

  const button = (
    <Button
      variant='danger'
      size={compact ? 'small' : 'medium'}
      disabled={!!blockedReason || mutation.isPending}
      onClick={() => setConfirmOpen(true)}
    >
      {__('Odstrániť z kategórie')}
    </Button>
  );

  return (
    <>
      {compact ? (
        <Stack gap={0.75} alignItems='flex-start'>
          <Typography
            component='b'
            sx={{ fontSize: 13, fontWeight: 700, color: 'primary.extraDark' }}
          >
            {__('Odstrániť z kategórie')}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {blockedReason ?? description}
          </Typography>
          {button}
        </Stack>
      ) : (
        // Desktop: karta sekcie s červeným rámom. Prečo sa nedá, stojí
        // v pätičke - popis nad tlačidlom hovorí, čo akcia robí, aj keď
        // práve nejde.
        <IssueSection
          tone='danger'
          title={__('Odstrániť z kategórie')}
          description={description}
          control={button}
          footer={
            blockedReason && (
              <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
                {blockedReason}
              </Typography>
            )
          }
        />
      )}

      <ConfirmationDialog
        open={confirmOpen}
        title={`${__('Odstrániť podnet z kategórie')} ${issue.category_name}?`}
        description={[
          `${__('Podnet zmizne z kategórie')} ${issue.category_name}. ${__(
            'Nález v nej zostane ako presunutý, aj s vyjadreniami.',
          )}`,
          triage
            ? `${__('Inú kategóriu podnet nemá, preto sa vráti na triáž do kategórie')} ${
                defaultCategory?.name ?? __('predvolenej')
              }.`
            : '',
          issue.status === 'accepted'
            ? __('Body, ktoré autor za prijatie dostal, mu zostanú.')
            : '',
          __('Späť ho vráti „Znovu prijať nález".'),
        ]
          .filter(Boolean)
          .join(' ')}
        confirmLabel={__('Odstrániť z kategórie')}
        isLoading={mutation.isPending}
        onConfirm={() => {
          if (!navigator.onLine) {
            toastError(__('Nie ste pripojený k internetu.'));
            return;
          }
          mutation.mutate(undefined);
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default IssueRemove;
